/**
 * The Users page's dialogs (Story 22, task 116): invite a user, edit a
 * user's role and scope, and the shared scope picker.
 *
 * With the organization tree (Story 48, task 208) each also picks the
 * user's home organization, limited to the ones the caller manages, and
 * the scope is "this organization and below" or Clients under that home.
 *
 * The API enforces every rule here — unique email, a non-empty scope for an
 * enabled record, the last-Admin guard — and its 409s are shown inline. The
 * form only avoids offering a submission the server is certain to refuse.
 */

import { useState } from 'react';

import type {
  ClientTenant,
  InviteUserBody,
  PlatformRole,
  PlatformUser,
  UpdateUserBody,
  UserScope,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { ErrorNote, INPUT, ModalShell } from '@bluesphere/mdr-alert-panel';
import { signInUrl } from '../auth/oidc';
import { ROLE_LABELS, ROLES } from '../lib/users';

const LABEL = 'text-xs uppercase tracking-wider text-bluesphere-textSecondary';

const ROLE_HINTS: Record<PlatformRole, string> = {
  admin: 'Everything on the Clients in scope; with all Clients, also Clients and users.',
  analyst: 'Read, triage and respond on the Clients in scope.',
  viewer: 'Read only, on the Clients in scope.',
};

/** An organization the caller may home a user at, indented by depth. */
export interface HomeChoice {
  id: string;
  label: string;
  depth: number;
}

function HomeSelect({
  value,
  onChange,
  homes,
}: {
  value: string;
  onChange: (id: string) => void;
  homes: HomeChoice[];
}) {
  return (
    <label className="block">
      <span className={LABEL}>Home organization</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className={INPUT}>
        {homes.map((h) => (
          <option key={h.id} value={h.id}>
            {'\u00a0\u00a0'.repeat(h.depth)}
            {h.label}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-bluesphere-muted">
        The organization they work for. Its suspension takes their access with it.
      </span>
    </label>
  );
}

// ----- Scope picker ----------------------------------------------------------

/**
 * All Clients, or a chosen set. Revoked Clients are not offered; an id
 * already in scope is kept even if it is not in the list, so an edit never
 * silently drops what it did not show (the server drops deleted Clients on
 * its own).
 */
export function ScopePicker({
  value,
  onChange,
  tenants,
  allLabel = 'All Clients, including ones added later',
}: {
  value: UserScope;
  onChange: (scope: UserScope) => void;
  tenants: ClientTenant[];
  /** "This organization and below" once the tree exists. */
  allLabel?: string;
}) {
  const selectable = tenants.filter((t) => t.status !== 'revoked');
  const chosen = value === 'all' ? [] : value;
  const toggle = (id: string) =>
    onChange(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);

  return (
    <fieldset className="space-y-2">
      <legend className={LABEL}>Clients in scope</legend>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-bluesphere-textOnLight">
        <input
          type="checkbox"
          checked={value === 'all'}
          onChange={(e) => onChange(e.target.checked ? 'all' : [])}
          className="h-4 w-4 rounded-sm border-bluesphere-borderMuted"
        />
        {allLabel}
      </label>
      {value !== 'all' && (
        <div className="max-h-48 space-y-1 overflow-y-auto rounded-input border border-bluesphere-border p-2">
          {selectable.length === 0 ? (
            <p className="text-xs text-bluesphere-muted">No Clients yet.</p>
          ) : (
            selectable.map((t) => (
              <label
                key={t.client_tenant_id}
                className="flex cursor-pointer items-center gap-2 text-sm text-bluesphere-textOnLight"
              >
                <input
                  type="checkbox"
                  checked={chosen.includes(t.client_tenant_id)}
                  onChange={() => toggle(t.client_tenant_id)}
                  className="h-4 w-4 rounded-sm border-bluesphere-borderMuted"
                />
                {t.display_name}
              </label>
            ))
          )}
        </div>
      )}
    </fieldset>
  );
}

function RoleSelect({
  value,
  onChange,
}: {
  value: PlatformRole;
  onChange: (role: PlatformRole) => void;
}) {
  return (
    <label className="block">
      <span className={LABEL}>Role</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as PlatformRole)}
        className={INPUT}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {ROLE_LABELS[r]}
          </option>
        ))}
      </select>
      <span className="mt-1 block text-xs text-bluesphere-muted">{ROLE_HINTS[value]}</span>
    </label>
  );
}

const scopeIsEmpty = (scope: UserScope) => scope !== 'all' && scope.length === 0;

function Footer({
  onCancel,
  submitLabel,
  pendingLabel,
  isPending,
  canSubmit,
}: {
  onCancel: () => void;
  submitLabel: string;
  pendingLabel: string;
  isPending: boolean;
  canSubmit: boolean;
}) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2">
      <PillButton variant="ghost" size="sm" type="button" onClick={onCancel}>
        Cancel
      </PillButton>
      <PillButton size="sm" type="submit" disabled={!canSubmit || isPending}>
        {isPending ? pendingLabel : submitLabel}
      </PillButton>
    </div>
  );
}

// ----- Invite ----------------------------------------------------------------

export function InviteUserModal({
  tenants,
  homes = [],
  defaultHome = null,
  clientsOf,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  tenants: ClientTenant[];
  /** Empty before the tree exists: no home picker. */
  homes?: HomeChoice[];
  defaultHome?: string | null;
  /** The Clients a home's scope may name. */
  clientsOf?: (homeId: string) => Set<string>;
  onCancel: () => void;
  onSubmit: (body: InviteUserBody) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<PlatformRole>('analyst');
  const [scope, setScope] = useState<UserScope>([]);
  const [home, setHome] = useState<string>(defaultHome ?? homes[0]?.id ?? '');
  const trimmed = email.trim();
  const homeClients = home && clientsOf ? clientsOf(home) : null;
  const offered = homeClients
    ? tenants.filter((t) => homeClients.has(t.client_tenant_id))
    : tenants;
  const canSubmit = /^[^@\s]+@[^@\s]+$/.test(trimmed) && !scopeIsEmpty(scope);

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">Invite a user</h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          A Simulint Microsoft account. The record binds the first time that account signs in; until
          then it shows as pending. No email is sent — copy the sign-in link to them afterwards.
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          const name = displayName.trim();
          onSubmit({
            email: trimmed,
            role,
            scope,
            ...(name ? { display_name: name } : {}),
            ...(homes.length > 0 && home ? { home_org_id: home } : {}),
          });
        }}
        className="space-y-4 px-6 py-5"
        autoComplete="off"
      >
        <label className="block">
          <span className={LABEL}>Email</span>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="first.last@simulint.com"
            className={INPUT}
          />
        </label>
        <label className="block">
          <span className={LABEL}>Display name (optional)</span>
          <input
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            className={INPUT}
          />
        </label>
        {homes.length > 0 && (
          <HomeSelect
            value={home}
            homes={homes}
            onChange={(id) => {
              setHome(id);
              // A list scope must stay inside the new home.
              if (scope !== 'all' && clientsOf) {
                const inside = clientsOf(id);
                setScope(scope.filter((t) => inside.has(t)));
              }
            }}
          />
        )}
        <RoleSelect value={role} onChange={setRole} />
        <ScopePicker
          value={scope}
          onChange={setScope}
          tenants={offered}
          allLabel={homes.length > 0 ? 'This organization and below' : undefined}
        />
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel="Invite"
          pendingLabel="Inviting…"
          isPending={isPending}
          canSubmit={canSubmit}
        />
      </form>
    </ModalShell>
  );
}

/** After an invite: the link to hand over, since the MVP sends no email. */
export function InvitedModal({ user, onClose }: { user: PlatformUser; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const url = signInUrl();

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard API can fail in non-secure contexts; the field is selectable */
    }
  }

  return (
    <ModalShell onClose={onClose} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          {user.email} is invited
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          Send them this link. They choose <strong>Sign in with Microsoft</strong> and use{' '}
          {user.email}; that first sign-in binds the record.
        </p>
      </div>
      <div className="px-6 py-5">
        <div className="flex items-stretch gap-2">
          <input
            readOnly
            value={url}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 rounded-input border border-bluesphere-borderMuted px-3 py-2 font-mono text-xs text-bluesphere-textOnLight"
          />
          <PillButton size="sm" onClick={copy}>
            {copied ? 'Copied' : 'Copy'}
          </PillButton>
        </div>
      </div>
      <div className="flex items-center justify-end border-t border-bluesphere-border px-6 py-3">
        <PillButton variant="ghost" size="sm" onClick={onClose}>
          Done
        </PillButton>
      </div>
    </ModalShell>
  );
}

// ----- Edit ------------------------------------------------------------------

/**
 * Role and scope. Only changed fields are sent. With `enabling`, the
 * change also enables the record: a disabled record may hold an empty
 * scope, so enabling it must carry a scope in the same request.
 */
export function EditUserModal({
  user,
  tenants,
  homes = [],
  clientsOf,
  enabling = false,
  onCancel,
  onSubmit,
  isPending,
  error,
}: {
  user: PlatformUser;
  tenants: ClientTenant[];
  homes?: HomeChoice[];
  clientsOf?: (homeId: string) => Set<string>;
  enabling?: boolean;
  onCancel: () => void;
  onSubmit: (body: UpdateUserBody) => void;
  isPending: boolean;
  error: unknown;
}) {
  const [role, setRole] = useState<PlatformRole>(user.role);
  const [scope, setScope] = useState<UserScope>(user.scope);
  const [home, setHome] = useState<string>(user.home_org_id ?? '');
  const homeClients = home && clientsOf ? clientsOf(home) : null;
  const offered = homeClients
    ? tenants.filter((t) => homeClients.has(t.client_tenant_id))
    : tenants;

  const scopeChanged = JSON.stringify(scope) !== JSON.stringify(user.scope);
  const homeChanged = homes.length > 0 && Boolean(home) && home !== user.home_org_id;
  const body: UpdateUserBody = {
    ...(role !== user.role ? { role } : {}),
    ...(scopeChanged ? { scope } : {}),
    ...(homeChanged ? { home_org_id: home } : {}),
    ...(enabling ? { enabled: true } : {}),
  };
  const mustHaveScope = user.enabled || enabling;
  const canSubmit = Object.keys(body).length > 0 && !(mustHaveScope && scopeIsEmpty(scope));

  return (
    <ModalShell onClose={onCancel} width="max-w-lg">
      <div className="border-b border-bluesphere-border px-6 py-5">
        <h2 className="text-lg font-semibold text-bluesphere-textOnLight">
          {enabling ? 'Enable' : 'Edit'} {user.display_name || user.email}
        </h2>
        <p className="mt-1 text-sm text-bluesphere-textSecondary">
          {enabling
            ? 'This record has no Clients in scope. Give it a scope to enable it.'
            : 'Takes effect for them within a minute.'}
        </p>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) onSubmit(body);
        }}
        className="space-y-4 px-6 py-5"
      >
        {homes.length > 0 && (
          <HomeSelect
            value={home}
            homes={
              homes.some((h) => h.id === home)
                ? homes
                : [{ id: home, label: 'Current home', depth: 0 }, ...homes]
            }
            onChange={(id) => {
              setHome(id);
              if (scope !== 'all' && clientsOf) {
                const inside = clientsOf(id);
                setScope(scope.filter((t) => inside.has(t)));
              }
            }}
          />
        )}
        <RoleSelect value={role} onChange={setRole} />
        <ScopePicker
          value={scope}
          onChange={setScope}
          tenants={offered}
          allLabel={homes.length > 0 ? 'This organization and below' : undefined}
        />
        <ErrorNote error={error} />
        <Footer
          onCancel={onCancel}
          submitLabel={enabling ? 'Enable' : 'Save'}
          pendingLabel="Saving…"
          isPending={isPending}
          canSubmit={canSubmit}
        />
      </form>
    </ModalShell>
  );
}
